import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useClientData } from '@/domains/clients/hooks/useClientData';
import { useSalesUnified } from '@/domains/sales/hooks/useSalesUnified';
import { SaleFormData, SaleItem } from '@/types/entities/sale';
import { X, Plus, ShoppingCart } from 'lucide-react';
import { StandardEntityDialog } from '@/components/common/dialogs/StandardEntityDialog';
import { cn } from '@/lib/utils';
import { iconStyles } from '@/styles/common-styles';
import { getValidRentalPointId } from '@/utils/userRentalPoint';
import { useAuth } from '@/context/AuthContext';
import { useStoreProductsUnified } from '@/hooks/store/useStoreProductsUnified';
import AddClientDialog from '@/domains/clients/components/AddClientDialog';
import { useStoreCategories } from '@/hooks/useCategoriesUnified';
import { usePaymentMethodOptions } from '@/hooks/domain-statuses/usePaymentMethods';
import { usePaymentStatuses } from '@/hooks/domain-statuses/usePaymentStatuses';

type AddSaleDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: SaleFormData) => void;
};

// SaleFormData is now imported from types

// Local type for component state (without total field)
type LocalSaleItem = {
  id: string;
  name: string;
  quantity: number;
  price: number;
  storeProductId?: string;
};

export const AddSaleDialog: React.FC<AddSaleDialogProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const { clients, isLoading: loading } = useClientData();
  const { createSale, isCreating } = useSalesUnified();
  const { user } = useAuth();
  const { products: storeProducts, isLoading: productsLoading } = useStoreProductsUnified();
  const { categoryOptionsWithAll: storeCategoryOptions, loading: storeCategoriesLoading } = useStoreCategories();
  const { options: paymentMethodOptions, isLoading: paymentMethodsLoading } = usePaymentMethodOptions(true);
  const { options: paymentStatusOptions, isLoading: paymentStatusesLoading, defaultStatus } = usePaymentStatuses();
  const [selectedClient, setSelectedClient] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [paymentStatusId, setPaymentStatusId] = useState<string>('');
  const [items, setItems] = useState<LocalSaleItem[]>([
    { id: '1', name: '', quantity: 1, price: 0 }
  ]);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isAddClientDialogOpen, setIsAddClientDialogOpen] = useState(false);
  // Global filters to help navigate large product lists
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [productSearch, setProductSearch] = useState<string>('');

  // Category options are provided and deduplicated by unified hook

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors([]);
    
    // Validation (клиент не обязателен)
    const errors: string[] = [];
    if (items.every(item => !item.name.trim() && !item.storeProductId)) errors.push('Добавьте хотя бы один товар');
    if (!paymentMethodId) errors.push('Выберите способ оплаты');
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (!user?.id) {
        setValidationErrors(['Пользователь не авторизован']);
        return;
      }
      // Resolve valid rental point for current user/session
      const rentalPointId = await getValidRentalPointId(user.id);
      const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
      const validItems = items.filter(item => (item.name && item.name.trim() !== '') || item.storeProductId);
      
      // Create sale using unified hook
      const saleItems: SaleItem[] = validItems.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        total: item.quantity * item.price,
        storeProductId: item.storeProductId
      }));
      
      await createSale({
        clientId: selectedClient || undefined,
        paymentMethodId: paymentMethodId,
        paymentStatusId: paymentStatusId || defaultStatus?.id,
        totalAmount,
        paidAmount: totalAmount, // Assuming full payment for sales
        notes: notes.trim() || undefined,
        rentalPointId,
        items: saleItems
      });
      
      // Also call onSave for backward compatibility
      const saleData: SaleFormData = {
        clientId: selectedClient || undefined,
        paymentMethodId,
        paymentStatusId: paymentStatusId || defaultStatus?.id,
        items: saleItems,
        total: totalAmount,
        notes: notes.trim(),
        createdAt: new Date()
      };
      await onSave(saleData);
      
      handleClose();
    } catch (error) {
      console.error('Error saving sale:', error);
      setValidationErrors(['Ошибка при сохранении продажи']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedClient('');
    setItems([{ id: '1', name: '', quantity: 1, price: 0 }]);
    setNotes('');
    onClose();
  };

  const handleNewClientCreated = (newClientId: string) => {
    setSelectedClient(newClientId);
    setIsAddClientDialogOpen(false);
  };

  const addItem = () => {
    const newId = (items.length + 1).toString();
    setItems([...items, { id: newId, name: '', quantity: 1, price: 0 }]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id: string, field: keyof LocalSaleItem, value: string | number | undefined) => {
    // Use functional update to avoid stale state when performing multiple updates in a row
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const onSelectProduct = (rowId: string, productId: string) => {
    const product = storeProducts.find(p => p.id === productId);
    if (!product) return;
    updateItem(rowId, 'storeProductId', productId);
    updateItem(rowId, 'name', product.name);
    updateItem(rowId, 'price', product.price || 0);
  };

  // Computed list with applied filters for better navigation
  const filteredProducts = useMemo(() => {
    const byCategory = selectedCategory === 'all'
      ? storeProducts
      : storeProducts.filter(p => p.categoryId === selectedCategory);
    const bySearch = productSearch.trim()
      ? byCategory.filter(p => p.name.toLowerCase().includes(productSearch.trim().toLowerCase()))
      : byCategory;
    return bySearch;
  }, [storeProducts, selectedCategory, productSearch]);

  const renderSaleContent = () => {
    if (loading || productsLoading || storeCategoriesLoading || paymentMethodsLoading || paymentStatusesLoading) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-muted-foreground">Загрузка...</div>
        </div>
      );
    }

    const totalAmount = items.reduce((sum, item) => {
      return sum + (item.quantity * item.price);
    }, 0);

    return (
      <div className="space-y-6 px-4 sm:px-6">
        {/* Client Selection and Payment Settings */}
        <div className="space-y-2">
          <Label htmlFor="client">Клиент</Label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите клиента" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.fullName || client.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsAddClientDialogOpen(true)}
            >
              Добавить клиента
            </Button>
          </div>
          <div className="mt-3">
            <Label className="text-xs text-muted-foreground">Способ оплаты</Label>
            <Select value={paymentMethodId} onValueChange={setPaymentMethodId}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите способ оплаты" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethodOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3">
            <Label className="text-xs text-muted-foreground">Статус оплаты</Label>
            <Select value={paymentStatusId} onValueChange={setPaymentStatusId}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите статус оплаты" />
              </SelectTrigger>
              <SelectContent>
                {paymentStatusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Items */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Товары</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addItem}
            >
              <Plus className="h-4 w-4 mr-2" />
              Добавить товар
            </Button>
          </div>

          {/* Global filters for product navigation */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="sm:w-64">
              <Label className="text-xs text-muted-foreground">Категория</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите категорию" />
                </SelectTrigger>
                <SelectContent>
                  {storeCategoryOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:flex-1">
              <Label className="text-xs text-muted-foreground">Поиск товара</Label>
              <Input
                placeholder="Начните вводить название..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
            </div>
          </div>

          {items.map((item) => (
            <div key={item.id} className="flex items-start gap-4 p-4 border rounded-lg">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-muted-foreground">Товар магазина</Label>
                <Select
                  value={item.storeProductId || ''}
                  onValueChange={(val) => onSelectProduct(item.id, val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите товар" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredProducts.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {typeof p.price === 'number' ? `— ${p.price}₽` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-24 space-y-1">
                <Label className="text-xs text-muted-foreground">Количество</Label>
                <Input
                  type="number"
                  placeholder="1"
                  value={item.quantity}
                  onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                  min="1"
                />
              </div>
              <div className="w-32 space-y-1">
                <Label className="text-xs text-muted-foreground">Цена (₽)</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={item.price}
                  onChange={(e) => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="pt-5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeItem(item.id)}
                  disabled={items.length === 1}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <Label htmlFor="notes">Примечания</Label>
          <Textarea
            id="notes"
            placeholder="Дополнительная информация..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </div>

        {/* Total */}
        <div className="flex justify-end">
          <div className="text-lg font-semibold">
            Итого: {totalAmount.toFixed(2)} руб.
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <StandardEntityDialog
        title="Добавить продажу"
        icon={<ShoppingCart className={cn(iconStyles.medium, "text-primary")} />}
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleSave}
        isLoading={isSubmitting || isCreating}
        validationErrors={validationErrors}
        size="standard"
      >
        {renderSaleContent()}
      </StandardEntityDialog>

      <AddClientDialog
        open={isAddClientDialogOpen}
        onOpenChange={setIsAddClientDialogOpen}
        onClientCreated={handleNewClientCreated}
      />
    </>
  );
};
